import React, { ReactElement, useEffect, useState } from 'react';
import { TrainingJob } from 'src/context/apiState';

import { StatusCell } from 'src/components/StatusCell';
import { apiGetRequest } from 'src/utils/mlPigeonAPIRequest';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { JSONViewer } from 'src/components/JSONViewer';
import { Link } from 'src/components/Link';
import { TrainingJobCell } from 'src/components/TrainingJobCell';
import { useParams } from 'react-router-dom';
import { Button } from '@cloudscape-design/components';

export const TrainingJobTable = ({
  loading,
  data,
  error,
}: {
  loading: boolean;
  data: TrainingJob[] | null;
  metricNames: Set<string> | null;
  error: Error | null;
}): ReactElement => {
  const { accountNo, applicationId, modelVersionId, modelArtifactId } =
    useParams();

  return (
    <ClientSideTable
      resizableColumns
      variant="container"
      actions={
        <Link
          to={`/accounts/${accountNo}/applications/${applicationId}/modelVersions/${modelVersionId}/modelArtifacts/${modelArtifactId}/dataQualityMetrics`}
        >
          View all data quality metrics
        </Link>
      }
      columnDefinitions={[
        {
          id: 'Job',
          header: 'Training Job',
          width: 450,
          cell: (item) => (
            <TrainingJobCell
              trainingJobArn={item.trainingJobArn}
              trainingJobName={item.trainingJobName}
              createdDate={item.createdDate}
              id={item.id}
            />
          ),

          sortingField: 'id',
          isRowHeader: true,
        },
        {
          id: 'operator-flow-status',
          header: 'Operator Flow Status',
          width: 220,
          cell: (item) => <StatusCell status={item.operatorFlowJobStatus} />,
          sortingField: 'operatorFlowJobStatus',
          isRowHeader: true,
        },
        {
          id: 'createdDate',
          header: 'Created Date',
          cell: (item) => {
            const date = new Date(item.createdDate * 1000);
            return date.toUTCString();
          },
          sortingField: 'createdDate',
          isRowHeader: true,
        },
        {
          id: 'sagemaker-config',
          header: 'Sagemaker Config',
          width: 400,
          cell: (item) => {
            if (!item) return <></>;
            const [viewConfig, setViewConfig] = useState(false);
            const [configData, setConfigData] = useState(null);
            const [loading, setLoading] = useState(true);
            const [error, setError] = useState<Error | null>(null);

            useEffect(() => {
              if (!viewConfig) return;
              apiGetRequest({
                endpoint: `jobDetails?jobIds=${item.id}`,
              })
                .then((data) => {
                  let json;
                  try {
                    json = JSON.parse(data?.[0]?.sageMakerApiPayload);
                  } catch (e) {
                    setError(new Error('Invalid JSON'));
                    return;
                  }

                  setConfigData(json);
                })
                .catch((err) => {
                  setError(err);
                })
                .finally(() => {
                  setLoading(false);
                });
            }, [item, item.id, viewConfig]);

            if (error)
              return <span className="error-text">{error?.message}</span>;
            return viewConfig ? (
              <JSONViewer loading={loading} jsonData={configData} />
            ) : (
              <Button
                onClick={() => {
                  setViewConfig(true);
                }}
              >
                View SageMaker Config
              </Button>
            );
          },
          isRowHeader: true,
        },
      ]}
      data={data ?? []}
      title={'Training Jobs'}
      error={error}
      loading={loading}
    />
  );
};
